import { Injectable } from "@angular/core";

type PrepareHandle = {
  complete: () => void;
};

type Barrier = {
  promise: Promise<void>;
  resolve: () => void;
};

/**
 * Defers APPOINTMENT_CREATED notify until post-create work (e.g. linking
 * template attachments) has finished for that appointment id.
 *
 * Call `prepare()` before `create()`, then `attach(id)` as soon as the id is
 * known, then `complete()` when linking is done (typically in `finally`).
 */
@Injectable({ providedIn: "root" })
export class AppointmentCreatedNotifyGate {
  readonly #pending = new Map<string, Promise<void>>();
  #prepare: Barrier | null = null;

  prepare(): PrepareHandle {
    let resolve!: () => void;
    const promise = new Promise<void>((r) => {
      resolve = r;
    });

    this.#prepare = { promise, resolve };

    return {
      complete: () => {
        resolve();

        if (this.#prepare?.promise === promise) {
          this.#prepare = null;
        }
      }
    };
  }

  attach(appointmentId: string): void {
    const prepared = this.#prepare;
    this.#prepare = null;

    if (!prepared) {
      return;
    }

    this.#pending.set(
      appointmentId,
      prepared.promise.finally(() => {
        if (this.#pending.get(appointmentId) === prepared.promise) {
          this.#pending.delete(appointmentId);
        }
      })
    );
  }

  async waitIfDeferred(appointmentId: string): Promise<void> {
    if (this.#prepare) {
      await this.#prepare.promise;

      return;
    }

    const pending = this.#pending.get(appointmentId);

    if (pending) {
      await pending;
    }
  }
}
